import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Alert, AlertTitle, AlertDescription } from '../../components/ui/alert';
import BackButton from '../../components/ui/BackNavigationButton';
import { Clock, AlertTriangle, ArrowLeft } from 'lucide-react';
import { prepareData } from '../../components/utils/generateScheduleUtils';
import LoadingPage from '../LoadingPage';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import { pages } from '../../components/utils/pages-links';
import TutorialOverlay from '../../components/ui/TutorialOverlay';
import { useTranslation } from 'react-i18next';
import SaveDraftButton from '../../components/generation/SaveDraftButton';
import ScenarioIndicator from '../../components/forms/scenario/ScenarioIndicator';

const GenerateSchedulePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [scenarioId, setScenarioId] = useState(null);

  const generateButtonRef = useRef(null);
  const isExampleMode = location.state?.isExample;
  const { t } = useTranslation();

  const tutorialSteps = [
    {
      title: t("tutorial.generate.title"),
      description: t("tutorial.generate.description"),
      position: "center",
    },
    {
      title: t("tutorial.generate.start.title"),
      description: t("tutorial.generate.start.description"),
      position: "top-right",
      elementRef: generateButtonRef,
    },
  ];

  useEffect(() => {
    const id = location.state?.scenarioId || (JSON.parse(sessionStorage.getItem('currentScenario'))?.id || null);
    setScenarioId(id);
  }, [location]);

  useEffect(() => {
    if (isExampleMode) {
      setShowTutorial(true);
    }
  }, [isExampleMode]);

  useEffect(() => {
    sessionStorage.removeItem('generationError');
    sessionStorage.removeItem('errorData');
  }, []);

  const handleBack = () => {
    navigate('/form/wsum', {
      state: { isExample: isExampleMode }
    });
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);

    sessionStorage.removeItem('generationError');
    sessionStorage.removeItem('errorData');

    console.log(JSON.stringify(prepareData()))

    try {
      let data = prepareData();

      if (isExampleMode) {
        data = {
          ...data,
          isExample: true
        };
      }

      const token = localStorage.getItem('token');

      const headers = {
        'Content-Type': 'application/json'
      };

      if (!isExampleMode) {
        if (!token) {
          throw new Error('Token not found');
        }
        headers['Authorization'] = `Bearer ${token}`;
      }

      const effectiveScenarioId = scenarioId || 1;



      const response = await fetch(`https://www.ia-schedule.com:4139/api/roster/generate?scenarioId=${effectiveScenarioId}`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(data)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to generate roster');
      }

      if (result.pythonResult?.status === 'INFEASIBLE' || result.pythonResult?.status === 'UNKNOWN') {
        sessionStorage.setItem('generationError', 'NO_SOLUTION');
        sessionStorage.setItem('errorData', JSON.stringify(data));

        setError({
          type: 'NO_SOLUTION',
          message: t("generate.error.noSolution.message")
        });
        return;
      }

      sessionStorage.setItem('rosterResult', JSON.stringify(result.pythonResult));
      navigate('/result', {
        state: { isExample: isExampleMode }
      });

    } catch (error) {
      console.error('Error generating schedule:', error);
      setError({
        type: 'ERROR',
        message: error.message
      });
    } finally {
      setIsGenerating(false);
    }
  };

  if (error?.type === 'NO_SOLUTION') {
    return (
      <div className='-mt-16'>
        <Breadcrumbs pages={pages} />
        <div className="p-6 bg-gray-100 min-h-screen">
          <div className="max-w-6xl mx-auto">
            <BackButton onClick={handleBack} />
            <Card className="mt-5">
              <CardContent className="p-12">
                <div className="flex flex-col items-center justify-center text-center space-y-8">
                  <AlertTriangle className="w-16 h-16 text-yellow-600" />

                  <div className="space-y-4">
                    <h1 className="text-3xl font-bold text-gray-900">
                      {t("generate.error.noSolution.title")}
                    </h1>

                    <Alert variant="warning" className="max-w-xl mx-auto">
                      <AlertTitle>{t("generate.error.noSolution.alertTitle")}</AlertTitle>
                      <AlertDescription>
                        {error.message}
                      </AlertDescription>
                    </Alert>

                    <div className="flex gap-4 justify-center mt-8">
                      <button
                        onClick={() => navigate('/form/collaborators')}
                        className="flex items-center gap-2 px-6 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                      >
                        <ArrowLeft className="w-5 h-5" />
                        {t("generate.error.noSolution.modifyButton")}
                      </button>
                      <SaveDraftButton
                        data={prepareData()}
                        translations={t}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  const handleNextTutorialStep = () => {
    if (tutorialStep < tutorialSteps.length - 1) {
      setTutorialStep(prev => prev + 1);
    } else {
      setShowTutorial(false);
    }
  };

  return (
    <div className='-mt-16'>
      <Breadcrumbs pages={pages} />
      <>
        {isGenerating && <LoadingPage translations={t} />}
        <div className="p-6 bg-gray-100 min-h-screen">
          <div className="max-w-6xl mx-auto">
            <BackButton onClick={handleBack} />

            <ScenarioIndicator />

            <Card className="mt-8">
              <CardContent className="p-12">
                <div className="flex flex-col items-center justify-center text-center space-y-8">
                  <Clock className="w-16 h-16 text-blue-600" />

                  <div className="space-y-4">
                    <h1 className="text-3xl font-bold text-gray-900">
                      {t("form.generate.title")}
                    </h1>
                    <p className="text-gray-600 max-w-md">
                      {t("form.generate.description")}
                    </p>

                    {error && (
                      <Alert variant="destructive" className="max-w-xl mx-auto">
                        <AlertTitle>Error</AlertTitle>
                        <AlertDescription>{error.message}</AlertDescription>
                      </Alert>
                    )}
                  </div>

                  <button
                    ref={generateButtonRef}
                    onClick={handleGenerate}
                    disabled={isGenerating}
                    className={`
                      flex items-center justify-center gap-3 
                      px-8 py-4 rounded-lg text-lg font-semibold
                      transition-all duration-200
                      ${isGenerating
                        ? 'bg-blue-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 hover:shadow-lg transform hover:-translate-y-0.5'
                      }
                      text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                    `}
                  >
                    <Clock className="w-5 h-5" />
                    {t("form.generate.button")}
                  </button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </>

      <TutorialOverlay
        isVisible={showTutorial}
        onClose={() => setShowTutorial(false)}
        title={tutorialSteps[tutorialStep]?.title}
        description={tutorialSteps[tutorialStep]?.description}
        position={tutorialSteps[tutorialStep]?.position}
        targetRef={tutorialSteps[tutorialStep]?.elementRef}
        onNext={handleNextTutorialStep}
        isLastStep={tutorialStep === tutorialSteps.length - 1}
      />
    </div>
  );
};

export default GenerateSchedulePage;